import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Tab, Tabs, Tooltip, Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap';
import ReactHighcharts from 'react-highcharts';
import { apiRequest, double } from './utils';

/**
 * Spend pacing chart for a single campaign.
 *
 * Shows the SHAPE of the spend next to the campaign's spend limits, so that a
 * campaign which is starving (flat at a fraction of its cap and never catching
 * up) is visually distinguishable from one that is pacing to budget. A single
 * "current spend" number plus a progress bar cannot show that - see NBI-22,
 * where campaign 1709 read $5.13 against a $9 hourly cap (healthy-looking)
 * while it was actually dead for most of every hour.
 *
 * Data source is the existing realtime report endpoint - no backend work:
 *
 *   GET /console/realtime_report?campaign_id=<code>&type=cp&interval=h&time_range=25
 *
 * `type=cp` sums the same `cp` (GROSS price) column the budget coordinator
 * reads for spend, so the bars reconcile with `current_spend` and with the
 * gross limits by construction rather than being a second opinion that drifts.
 *
 * NOTE on the gross/net caveat: `PRICE_FIELD` in insights
 * `service/realtime_ch.js` switches to `bp` (net) for the `publisher` role,
 * which would not line up with the gross limits. The `publisher` role has no
 * access to `adcampaigns` (see components/roles.json), and this widget is only
 * mounted inside the non-advertiser branch of the campaign form, so every role
 * that can reach it resolves `PRICE_FIELD` to gross `cp`.
 */

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Bar colors. Muted blue-grey reads as "not delivering" without shouting;
// green is the same "within limit" signal progressBar() uses, orangered the
// same "over limit" signal.
const COLOR_UNDER = '#546e7a';
const COLOR_ON_PACE = '#49b27b';
const COLOR_OVER = 'orangered';
const COLOR_NO_LIMIT = '#009fdb';
const COLOR_THRESHOLD = '#f02d5b';
const COLOR_MUTED = '#aaaaaa';
const COLOR_GRID = '#555555';
const FONT = 'Roboto, Helvetica, Arial, sans-serif';

// A completed bucket at or above this fraction of its target counts as pacing.
// Below it the campaign is leaving budget on the table, which is the NBI-22
// signature.
export const ON_PACE_FRACTION = 0.6;

/**
 * The views this widget can render. `buckets` x `stepMs` is the visible window;
 * `timeRangeHours` is deliberately one step LARGER so the oldest visible
 * bucket is fully covered by the query (the backend's WITH FILL starts at
 * `now() - time_range`, which is not aligned to a bucket boundary).
 *
 * `thresholdSource` names which campaign limit this view is measured against.
 * The 5-minute view divides the hourly cap by 12 because that is the per-5-min
 * budget an evenly paced campaign should be spending.
 */
// Ordered shortest window first, which is the order the range tabs render in.
//
// The ids are GRAIN-based ('min5', 'hour', 'day') while the labels are
// RANGE-based, and the mismatch is deliberate: the labels are what an advertiser
// reads and "Last 24 hours" is what they asked for, but the ids are referenced by
// both campaign forms and by this component's tests, so renaming them would be
// churn with no behavioural gain. `last_hour` is the one addition.
export const PACING_VIEWS = [
  {
    id: 'last_hour',
    label: 'Last hour',
    interval: 'm',
    stepMs: 5 * MINUTE_MS,
    buckets: 12,
    timeRangeHours: 2,
    xLabelFormat: '{value:%H:%M}',
    xTickInterval: 15 * MINUTE_MS,
    thresholdSource: 'hourly',
    thresholdDivisor: 12,
    thresholdLabel: 'hourly cap / 12',
    bucketNoun: '5-min slot'
  },
  {
    id: 'min5',
    label: 'Last 3 hours',
    interval: 'm',
    stepMs: 5 * MINUTE_MS,
    buckets: 36,
    timeRangeHours: 4,
    xLabelFormat: '{value:%H:%M}',
    xTickInterval: 30 * MINUTE_MS,
    thresholdSource: 'hourly',
    thresholdDivisor: 12,
    thresholdLabel: 'hourly cap / 12',
    bucketNoun: '5-min slot',
    capComparable: true
  },
  {
    id: 'hour',
    label: 'Last 24 hours',
    interval: 'h',
    stepMs: HOUR_MS,
    buckets: 24,
    timeRangeHours: 25,
    xLabelFormat: '{value:%H:%M}',
    xTickInterval: 3 * HOUR_MS,
    thresholdSource: 'hourly',
    thresholdDivisor: 1,
    thresholdLabel: 'hourly cap',
    bucketNoun: 'hour',
    capComparable: true
  },
  {
    id: 'day',
    label: 'Last 7 days',
    interval: 'd',
    stepMs: DAY_MS,
    buckets: 7,
    timeRangeHours: 8 * 24,
    xLabelFormat: '{value:%e %b}',
    xTickInterval: DAY_MS,
    thresholdSource: 'daily',
    thresholdDivisor: 1,
    thresholdLabel: 'daily cap',
    bucketNoun: 'day',
    // The ONE view whose buckets and whose cap use different day boundaries, so
    // it shows volume only: no threshold line, no per-bar cap colouring, no
    // pacing verdict.
    //
    // These buckets are UTC days - toStartOfInterval(ts, toIntervalDay(1)) in
    // insights service/realtime_ch.js, which takes no timezone - while the daily
    // spend cap resets on the campaign's OWN flight_timezone day: the budget
    // coordinator groups campaigns by timezone and passes each one into its daily
    // query (service/etl/budget_coordinator.js getDailyImpressionsReportByCampaign).
    // For an Eastern campaign a UTC day runs 20:00 to 20:00 Eastern, so every bar
    // straddles two cap periods.
    //
    // Comparing them painted bars orangered "over cap" on campaigns that never
    // breached their cap, purely from where the split fell. The hourly views have
    // no such problem: the coordinator's hourly window is explicitly UTC too.
    //
    // The range TOTAL stays valid - a sum over 7 UTC days is still a 7-day total;
    // only the outermost bars' edges shift.
    capComparable: false
  }
];

export const DEFAULT_VIEW_ID = 'hour';

/**
 * Falls back to DEFAULT_VIEW_ID rather than to PACING_VIEWS[0]. Positional
 * fallback silently changed meaning when `last_hour` was inserted at the front -
 * an unknown id would have started resolving to a 1-hour window instead of the
 * 24-hour default both campaign forms open on.
 */
export function findView (viewId) {
  return PACING_VIEWS.find(v => v.id === viewId) ||
    PACING_VIEWS.find(v => v.id === DEFAULT_VIEW_ID);
}

/**
 * Spend across the WHOLE window, including the bucket still in progress.
 *
 * Deliberately not `pacingSummary().total`, which excludes the current bucket so
 * its pacing verdict is not dragged down by a partial one. That is right for a
 * verdict and wrong for a headline: "Last hour" would read $0.00 for the first
 * five minutes of every hour, on a campaign that is delivering fine.
 */
export function rangeTotal (points) {
  return (points || []).reduce((sum, p) => sum + (p && p.y > 0 ? p.y : 0), 0);
}

/**
 * Whether the range total is a real number worth showing.
 *
 * A failed or not-yet-arrived fetch leaves `points` empty, and rangeTotal([]) is
 * 0 - which would render "$0.00 spend - last 24 hours" beside the words "Spend
 * pacing unavailable". A confident zero on missing data is exactly the failure
 * NBU-7 was about: a number the system cannot actually vouch for. Absent beats
 * wrong, so the total waits for a series.
 */
export function shouldShowRangeTotal ({ loading, error, points }) {
  if (error) return false;
  if (loading && (!points || points.length === 0)) return false;
  return Array.isArray(points) && points.length > 0;
}

/**
 * ClickHouse returns bucket timestamps as UTC datetimes with no zone marker
 * ("2026-08-13 15:00:00"). `new Date()` on that string is parsed as LOCAL time
 * by browsers, which silently shifts the whole series by the UTC offset, so the
 * separator has to be normalized and the zone made explicit.
 */
export function parseBucketTimestamp (value) {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  const text = String(value).trim();
  if (!text) return null;
  const iso = /[Zz]|[+-]\d{2}:?\d{2}$/.test(text)
    ? text.replace(' ', 'T')
    : `${text.replace(' ', 'T')}Z`;
  const ms = new Date(iso).getTime();
  return isNaN(ms) ? null : ms;
}

function roundCurrency (value) {
  if (value === undefined || value === null || isNaN(value)) return 0;
  return Math.round(Number(value) * 1000) / 1000;
}

/**
 * Snap `nowMs` down to the start of the bucket that contains it. Epoch 0 is a
 * UTC midnight, so flooring by the step also aligns day buckets to UTC days -
 * the same boundary `toStartOfInterval(ts, toIntervalDay(1))` uses.
 */
export function currentBucketStart (stepMs, nowMs) {
  return Math.floor(nowMs / stepMs) * stepMs;
}

/**
 * Turn the endpoint's `datarows` ([[bucket, value], ...]) into exactly
 * `buckets` points ending at the bucket containing `nowMs`.
 *
 * Every bucket in the window is emitted, so an hour with no spend renders as a
 * visible zero bar instead of vanishing from the series - the dead hour IS the
 * signal. The backend's WITH FILL stops at `toStartOfHour(now())` (exclusive),
 * so a dead CURRENT bucket comes back missing rather than zero; normalizing
 * client-side against a generated bucket list covers that and makes the widget
 * independent of the backend's fill range.
 *
 * Values are summed into their bucket, which also collapses the 1-minute rows
 * the backend returns for `interval=m` into 5-minute buckets.
 */
export function normalizeSeries (datarows, { stepMs, buckets, nowMs }) {
  const endMs = currentBucketStart(stepMs, nowMs);
  const startMs = endMs - (buckets - 1) * stepMs;
  const sums = new Map();
  for (const row of datarows || []) {
    if (!Array.isArray(row)) continue;
    const ms = parseBucketTimestamp(row[0]);
    if (ms == null) continue;
    const bucket = currentBucketStart(stepMs, ms);
    if (bucket < startMs || bucket > endMs) continue;
    const value = Number(row[1]);
    sums.set(bucket, (sums.get(bucket) || 0) + (isNaN(value) ? 0 : value));
  }
  const points = [];
  for (let i = 0; i < buckets; i++) {
    const x = startMs + i * stepMs;
    points.push({ x, y: roundCurrency(sums.get(x) || 0) });
  }
  return points;
}

/**
 * Classify the window against its target. The last bucket is always in
 * progress, so it is excluded from the verdict - including it would report
 * every campaign as under-delivering.
 *
 * The verdict is driven by AVERAGE utilization, not by the peak. Judging on the
 * peak makes a single spike outrank the whole window: campaign 1709's real
 * NBI-22 series averages 10% of its $9 cap but touches $9.51 once, and a
 * peak-first rule reports that as "over cap" - hiding the 23 starved hours that
 * are the actual problem. Breaching the cap in one bucket is a separate,
 * secondary fact, reported as `bucketsOverTarget` and colored per bar.
 *
 * Statuses:
 *   no-limit  - nothing to measure against
 *   idle      - no spend at all in the window
 *   over      - completed buckets averaged ABOVE the target (sustained overspend)
 *   starved   - completed buckets averaged under ON_PACE_FRACTION of target
 *   on-pace   - everything else
 */
export function pacingSummary (points, target) {
  const completed = (points || []).slice(0, -1);
  const total = completed.reduce((sum, p) => sum + (p.y || 0), 0);
  const emptyBuckets = completed.filter(p => !(p.y > 0)).length;
  const base = { total, emptyBuckets, completedCount: completed.length };

  if (!(target > 0) || completed.length === 0) {
    return { ...base, status: 'no-limit', utilization: null, bucketsOverTarget: 0 };
  }

  const utilization = (total / completed.length) / target;
  const bucketsOverTarget = completed.filter(p => p.y > target).length;
  let status;
  if (total <= 0) status = 'idle';
  else if (utilization > 1) status = 'over';
  else if (utilization < ON_PACE_FRACTION) status = 'starved';
  else status = 'on-pace';

  return { ...base, status, utilization, bucketsOverTarget };
}

export function barColor (value, target) {
  if (!(target > 0)) return COLOR_NO_LIMIT;
  if (value > target) return COLOR_OVER;
  if (value >= target * ON_PACE_FRACTION) return COLOR_ON_PACE;
  return COLOR_UNDER;
}

const STATUS_TEXT = {
  'no-limit': { label: 'NO CAP SET', color: COLOR_MUTED },
  idle: { label: 'NO SPEND', color: COLOR_MUTED },
  over: { label: 'OVER CAP', color: COLOR_OVER },
  starved: { label: 'UNDER-DELIVERING', color: COLOR_OVER },
  'on-pace': { label: 'PACING', color: COLOR_ON_PACE }
};

/**
 * Target spend per bucket for a view, given the campaign's limits.
 *
 * 0 for a view whose buckets are not on the same boundary as its cap (see the
 * `day` view's note). 0 is already the "nothing to compare against" value
 * everywhere downstream: barColor returns the neutral blue, buildPacingChartConfig
 * draws no threshold line, and pacingSummary reports `no-limit` rather than a
 * verdict. So one flag disables the whole comparison without a second code path.
 */
export function viewTarget (view, { hourlyLimit, dailyLimit }) {
  if (view && view.capComparable === false) return 0;
  const limit = view.thresholdSource === 'daily' ? dailyLimit : hourlyLimit;
  if (!(limit > 0)) return 0;
  return Number(limit) / view.thresholdDivisor;
}

export function buildPacingChartConfig (points, { view, target, height }) {
  // Without an explicit max, Highcharts scales to the data and a threshold well
  // above a starving campaign's bars falls outside the plot area - the exact
  // case the line exists for. Headroom keeps the line and the tallest bar both
  // on screen.
  const peak = points.reduce((max, p) => Math.max(max, p.y || 0), 0);
  const axisMax = Math.max(peak, target || 0) * 1.15;

  return {
    chart: {
      type: 'column',
      height: height,
      backgroundColor: 'none',
      borderWidth: 0,
      spacing: [4, 6, 2, 2],
      animation: false
    },
    // Bucket boundaries are UTC (the hourly budget window resets on the UTC
    // hour); rendering in local time only relabels them.
    time: { useUTC: false },
    title: { text: '' },
    subtitle: { text: '' },
    credits: { enabled: false },
    xAxis: {
      type: 'datetime',
      tickInterval: view.xTickInterval,
      labels: { format: view.xLabelFormat, style: { color: COLOR_MUTED, fontFamily: FONT, fontSize: '9px' } },
      lineColor: COLOR_GRID,
      tickColor: COLOR_GRID
    },
    yAxis: {
      min: 0,
      max: axisMax > 0 ? axisMax : null,
      title: { text: '' },
      gridLineColor: COLOR_GRID,
      labels: {
        // A formatter rather than a '${value}' format string: the literal form
        // trips eslint's no-template-curly-in-string, and double() gives the
        // same currency formatting the rest of the campaign screen uses.
        formatter: function () { return double(this.value); },
        style: { color: COLOR_MUTED, fontFamily: FONT, fontSize: '9px' }
      },
      // The line carries no text label on purpose. At 130px tall there is no
      // corner of the plot a label can sit in without landing on top of the
      // bars, which is exactly where the eye needs to be; the cap value goes in
      // the caption underneath instead.
      plotLines: target > 0 ? [{
        value: target,
        color: COLOR_THRESHOLD,
        dashStyle: 'ShortDash',
        width: 2,
        zIndex: 5
      }] : []
    },
    legend: { enabled: false },
    tooltip: {
      useHTML: false,
      formatter: function () {
        const stamp = ReactHighcharts.Highcharts.dateFormat('%e %b %H:%M', this.x);
        const spend = double(this.y);
        const share = target > 0 ? ` (${Math.round(100 * this.y / target)}% of ${view.thresholdLabel})` : '';
        return `${stamp}<br/><b>${spend}</b>${share}`;
      }
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        pointPadding: 0.05,
        groupPadding: 0.05,
        // A zero bucket has no bar to draw at all. A 3px stub keeps dead buckets
        // legible as a row of bars sitting on the axis rather than as missing
        // data; at 1px they are indistinguishable from the axis line itself.
        minPointLength: 3
      },
      series: { animation: false, states: { hover: { brightness: 0.2 } } }
    },
    series: [{
      name: 'Gross spend',
      // The last bucket is still filling, so it is drawn faded. At full opacity
      // a half-finished hour reads as a short hour, which is the same false
      // signal the verdict deliberately excludes it to avoid.
      data: points.map((p, i) => ({
        x: p.x,
        y: p.y,
        color: barColor(p.y, target),
        opacity: i === points.length - 1 ? 0.45 : 1
      })),
      turboThreshold: 0
    }]
  };
}

function buildReportPath (campaignId, view) {
  const params = new URLSearchParams({
    campaign_id: String(campaignId),
    type: 'cp',
    interval: view.interval,
    time_range: String(view.timeRangeHours)
  });
  return `/realtime_report?${params.toString()}`;
}

/** Fetch + normalize one view's series. Exported for tests. */
export async function fetchPacingSeries (campaignId, view, nowMs = Date.now()) {
  const result = await apiRequest(buildReportPath(campaignId, view));
  const datarows = (result && Array.isArray(result.datarows)) ? result.datarows : [];
  return normalizeSeries(datarows, { stepMs: view.stepMs, buckets: view.buckets, nowMs });
}

function StatusChip ({ summary, view }) {
  // A view with no comparable cap must not borrow "NO CAP SET": the campaign's
  // daily cap usually IS set, it simply is not measurable against a UTC day.
  // Saying otherwise would send an advertiser to fill in a limit they already have.
  if (view.capComparable === false) {
    return (
      <Tooltip title={
        `Bars are UTC days. This campaign's daily cap resets on its own timezone's day, `
        + `so a UTC day straddles two cap periods and the two are not comparable - `
        + `the bars show volume only. Use Last 24 hours to pace against the hourly cap.`
      }>
        <span style={{ color: COLOR_MUTED, fontSize: '10px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
          UTC DAYS
        </span>
      </Tooltip>
    );
  }
  const text = STATUS_TEXT[summary.status] || STATUS_TEXT['no-limit'];
  const pct = summary.utilization != null ? ` ${Math.round(100 * summary.utilization)}%` : '';
  const tip = summary.utilization != null
    ? `Completed ${view.bucketNoun}s averaged ${Math.round(100 * summary.utilization)}% of the ${view.thresholdLabel}. `
      + `${summary.emptyBuckets} of ${summary.completedCount} had no spend`
      + `${summary.bucketsOverTarget > 0 ? `, ${summary.bucketsOverTarget} went over the cap` : ''}. `
      + `The in-progress ${view.bucketNoun} is excluded.`
    : `No ${view.thresholdSource} cap is set, so there is nothing to pace against.`;
  return (
    <Tooltip title={tip}>
      <span style={{ color: text.color, fontSize: '10px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
        {text.label}{pct}
      </span>
    </Tooltip>
  );
}

/**
 * One chart plus its caption. Owns no fetching - the container passes the
 * already-normalized points in so the inline widget and the dialog share a
 * single request for the default view.
 */
function PacingChartPanel ({ points, view, target, height, loading, error }) {
  const summary = useMemo(() => pacingSummary(points, target), [points, target]);
  const config = useMemo(
    () => buildPacingChartConfig(points, { view, target, height }),
    [points, view, target, height]
  );

  if (error) {
    return <div style={{ height, display: 'flex', alignItems: 'center', color: COLOR_MUTED, fontSize: '11px' }}>{error}</div>;
  }
  if (loading && points.length === 0) {
    return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={20} /></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '2px' }}>
        <StatusChip summary={summary} view={view} />
        <span style={{ color: COLOR_MUTED, fontSize: '10px' }}>
          {double(summary.total)} over {summary.completedCount} completed {view.bucketNoun}s
          {summary.emptyBuckets > 0 ? ` · ${summary.emptyBuckets} with no spend` : ''}
          {summary.bucketsOverTarget > 0 ? ` · ${summary.bucketsOverTarget} over cap` : ''}
        </span>
      </div>
      <ReactHighcharts config={config} isPureConfig={true} />
      {target > 0 && (
        <div style={{ color: COLOR_THRESHOLD, fontSize: '9px', marginTop: '-2px' }}>
          &#9476;&#9476; {view.thresholdLabel} {double(target)}
        </div>
      )}
    </div>
  );
}

/**
 * Inline pacing widget for the campaign form, with a click-through dialog that
 * adds the views this instance is not showing.
 *
 * The 5-minute view matters because hourly bars average away intra-hour
 * breakage: NBI-22's campaign was flat for ~45 minutes, burst, then went dead,
 * which an hourly bar renders as a perfectly ordinary $5.13.
 *
 * The selected range is ONE piece of state shared by the inline widget and the
 * dialog. It used to be a fixed `viewId` prop for inline plus a separate
 * `dialogViewId` state, which meant the range tabs existed but were reachable
 * only by expanding, and a range chosen in the dialog was forgotten on close.
 *
 * Props:
 *   campaignId   - campaign `code`; nothing renders without it (create screen)
 *   viewId       - PACING_VIEWS entry to OPEN on; the user can change it
 *   hourlyLimit  - `hourly_revenue_limit`, gross USD
 *   dailyLimit   - `daily_revenue_limit`, gross USD
 *   title        - optional header text
 *   width        - optional CSS max-width for the widget
 *   showRangeTabs - render the range selector inline (default true)
 *   onRangeChange - called with {id, label, total, ready} whenever the selected
 *                   range or its data changes, so the caller can render the
 *                   headline figure alongside the cap cards
 */
export default function SpendPacingChart ({
  campaignId, viewId = DEFAULT_VIEW_ID, hourlyLimit, dailyLimit,
  title = 'Spend pacing', width = '480px', showRangeTabs = true, onRangeChange
}) {
  const [selectedViewId, setSelectedViewId] = useState(viewId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Keyed by view id: a range already fetched is not fetched again when the user
  // switches back to it. Previously only the dialog cached.
  const [seriesByView, setSeriesByView] = useState({});

  const containerRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const inlineView = findView(selectedViewId);
  const points = seriesByView[inlineView.id] || [];
  const limits = { hourlyLimit, dailyLimit };

  // react-admin v3 renders every FormTab and hides the inactive ones with
  // `display: none`, so mounting alone does not mean the user is looking at
  // this. A hidden element never intersects, which makes the observer a cheap
  // "fetch when actually shown" gate.
  useEffect(() => {
    if (visible) return undefined;
    if (typeof IntersectionObserver !== 'function' || !containerRef.current) {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) setVisible(true);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [visible]);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setError(null);
    try {
      const series = await fetchPacingSeries(campaignId, inlineView);
      if (!mountedRef.current) return;
      setSeriesByView(prev => ({ ...prev, [inlineView.id]: series }));
    } catch (e) {
      if (mountedRef.current) setError('Spend pacing unavailable');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [campaignId, inlineView]);

  // Refetches when the selected range changes, because `load` closes over
  // inlineView. A range already in the cache still refetches on an explicit
  // Refresh, which is what Refresh is for.
  useEffect(() => {
    if (visible && !seriesByView[inlineView.id]) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, load, inlineView.id]);

  const openDialog = () => setDialogOpen(true);

  const selectView = (nextViewId) => setSelectedViewId(nextViewId);

  // The selected range's headline number is rendered by the delivery-cards row as
  // a fourth card, so all four squares share one visual language. This widget owns
  // the range state and the series, so it reports the figure upward rather than
  // the parent re-deriving it - there is one source for what "the selected range"
  // currently means.
  const total = rangeTotal(points);
  const ready = shouldShowRangeTotal({ loading, error, points });
  useEffect(() => {
    if (onRangeChange) onRangeChange({ id: inlineView.id, label: inlineView.label, total, ready });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlineView.id, inlineView.label, total, ready]);

  if (!campaignId) return null;

  const rangeTabs = showRangeTabs ? (
    <Tabs
      value={inlineView.id}
      onChange={(e, v) => selectView(v)}
      indicatorColor="primary"
      textColor="primary"
      variant="scrollable"
      scrollButtons="off"
      style={{ minHeight: '28px' }}
    >
      {PACING_VIEWS.map(v => (
        <Tab key={v.id} value={v.id} label={v.label}
          style={{ minHeight: '28px', minWidth: 'auto', padding: '0 10px', fontSize: '10px' }} />
      ))}
    </Tabs>
  ) : null;

  return (
    <div ref={containerRef} style={{ width: '100%', maxWidth: width, marginTop: '4px', marginBottom: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Typography variant="caption" style={{ color: COLOR_MUTED, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {title} &middot; {inlineView.label}
        </Typography>
        <span style={{ marginLeft: 'auto' }} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load} disabled={loading} style={{ color: COLOR_MUTED, padding: '2px' }}>
            <RefreshIcon style={{ fontSize: '14px' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Expand">
          <IconButton size="small" onClick={openDialog} style={{ color: COLOR_MUTED, padding: '2px' }}>
            <ZoomOutMapIcon style={{ fontSize: '14px' }} />
          </IconButton>
        </Tooltip>
      </div>

      {rangeTabs}

      {/* The range total, from the series already fetched for the chart - no
          extra request. It carries NO cap, and that is not an omission: the caps
          are hourly, daily and lifetime, so none of them bounds an arbitrary
          range. The cap comparisons stay on CampaignDeliveryCards, which reads
          the budget coordinator's own counters. Showing a ClickHouse range total
          against an hourly cap would be a number that disagrees with the gate,
          which is the class of bug NBU-7 existed to fix. */}
      <div onClick={openDialog} style={{ cursor: 'pointer' }}>
        <PacingChartPanel
          points={points}
          view={inlineView}
          target={viewTarget(inlineView, limits)}
          height={130}
          loading={loading}
          error={error}
        />
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle disableTypography>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6">Spend pacing &middot; campaign {campaignId}</Typography>
            <span style={{ marginLeft: 'auto' }} />
            <IconButton size="small" onClick={load} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setDialogOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          {/* Same selected range as the inline widget, so expanding shows what
              was already on screen and a range picked here survives the close. */}
          <Tabs
            value={inlineView.id}
            onChange={(e, v) => selectView(v)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            {PACING_VIEWS.map(v => <Tab key={v.id} value={v.id} label={v.label} />)}
          </Tabs>
          <div style={{ paddingTop: '12px' }}>
            <PacingChartPanel
              points={points}
              view={inlineView}
              target={viewTarget(inlineView, limits)}
              height={340}
              loading={loading}
            />
            <Typography variant="caption" style={{ color: COLOR_MUTED }}>
              <strong>{double(total)}</strong> gross spend over the {inlineView.label.toLowerCase()}, from the
              same <code>cp</code> column the budget coordinator reads, so these bars reconcile with the
              current-spend figures on the cards. This total is not measured against a cap: the campaign's caps are
              hourly, daily and lifetime, and none of them bounds an arbitrary range. Bucket boundaries are UTC,
              labelled in your local timezone. Bars are colored against the {inlineView.thresholdLabel}: blue-grey
              under&nbsp;{Math.round(100 * ON_PACE_FRACTION)}%, green on pace, orange over cap.
            </Typography>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
